/**
 * UI Slice
 * 
 * Manages shared UI state across the application:
 * - Theme preference
 * - Mobile menu state
 * 
 * Sidebar state lives in sidebarSlice (sidebarState: 'expanded' | 'collapsed').
 * This slice is persisted to localStorage to remember user preferences.
 */

import type { StateCreator } from 'zustand';
import type { BoundStoreState, UISlice, ThemeMode } from '../types';

// ============================================================================
// Initial State
// ============================================================================

export const uiSliceInitialState = {
  theme: 'system' as ThemeMode,
  isMobileMenuOpen: false,
};

// ============================================================================
// Slice Creator
// ============================================================================

/**
 * Creates the UI slice.
 * 
 * @param set - Zustand set function
 * @param get - Zustand get function (for cross-slice access)
 */
export const createUISlice: StateCreator<
  BoundStoreState,
  [['zustand/devtools', never]],
  [],
  UISlice
> = (set) => ({
  // State
  ...uiSliceInitialState,

  // Actions
  setTheme: (theme: ThemeMode) =>
    set(
      { theme },
      undefined,
      'ui/setTheme'
    ),

  toggleMobileMenu: () =>
    set(
      (state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen }),
      undefined,
      'ui/toggleMobileMenu'
    ),

  setMobileMenuOpen: (open: boolean) =>
    set(
      { isMobileMenuOpen: open },
      undefined,
      'ui/setMobileMenuOpen'
    ),
});

// ============================================================================
// Persistence Configuration
// ============================================================================

/**
 * Keys to persist from the UI slice.
 * Only preferences that should survive page refresh.
 */
export const uiSlicePersistKeys: (keyof UISlice)[] = [
  'theme',
];
